import {useState} from "react";
import {Button} from "../../../basic/button.tsx";
import {Card} from "../../../basic/card.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {CreateLicenseDialog} from "../../../dialogs/admin/CreateLicenseDialog";
import {EditLicenseDialog} from "../../../dialogs/admin/EditLicenseDialog";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";
import {LicenseTable} from "./LicenseTable.tsx";
import {LicenseActions} from "./LicenseActions.tsx";
import {useGetLicenseByIdQuery, useGetLicensesQuery, VwpLicense} from "../../../../store/generated/api.ts";

export function AdminLicensesView() {
    const {t} = useTranslationWithKeys();
    const [editLicenseDialogOpen, setEditLicenseDialogOpen] = useState(false);
    const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);
    const [createLicenseDialogOpen, setCreateLicenseDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const {
        data: licensesData,
        isLoading,
        error,
        refetch
    } = useGetLicensesQuery({
        page: currentPage,
        size: pageSize,
        ...(searchTerm && {search: searchTerm})
    });

    const licenses = licensesData?.licenses || [];
    const pagination = licensesData?.pagination;

    const {
        data: selectedLicenseData,
        isLoading: isLicenseLoading,
        error: licenseError
    } = useGetLicenseByIdQuery(
        {licenseId: selectedLicenseId || ''},
        {skip: !selectedLicenseId}
    );

    const handleLicenseClick = (license: VwpLicense) => {
        setSelectedLicenseId(license.id);
        setEditLicenseDialogOpen(true);
    };

    const handleCreateLicense = () => {
        setCreateLicenseDialogOpen(true);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page - 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.licenses.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.licenses.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <p>{t('common.loading')}</p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.licenses.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.licenses.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <p className="text-red-500">{t('common.error')}</p>
                                <Button onClick={() => refetch()} className="mt-2">
                                    {t('common.retry')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('admin.licenses.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('admin.licenses.description')}</p>
            </div>

            <Card className="shadow-sm border-border/50">
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <SearchInput
                                placeholder={t('admin.licenses.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <LicenseActions
                                totalLicenses={pagination?.total || 0}
                                onCreateLicense={handleCreateLicense}
                            />
                        </div>
                        <LicenseTable
                            licenses={licenses}
                            onLicenseClick={handleLicenseClick}
                        />

                        {pagination && (
                            <PaginationControls
                                currentPage={pagination.page + 1}
                                totalPages={pagination.totalPages}
                                pageSize={pagination.size}
                                pageSizeOptions={[10, 25, 50, 100]}
                                paginationInfo={{
                                    start: pagination.page * pagination.size + 1,
                                    end: Math.min((pagination.page + 1) * pagination.size, pagination.total),
                                    total: pagination.total
                                }}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <EditLicenseDialog
                open={editLicenseDialogOpen}
                onOpenChange={(open) => {
                    setEditLicenseDialogOpen(open);
                    if (!open) {
                        setSelectedLicenseId(null);
                    }
                }}
                selectedLicense={selectedLicenseData || null}
                isLoading={isLicenseLoading}
                error={licenseError}
            />

            <CreateLicenseDialog
                open={createLicenseDialogOpen}
                onOpenChange={setCreateLicenseDialogOpen}
            />
        </div>
    );
}